import { useState } from "react";
import { UserRound } from "lucide-react";
import { useMe, useSetUserDisabled, useUsers } from "@/api/hooks";
import { errorMessage } from "@/api/client";
import type { AdminUser } from "@/api/types";
import { timeAgo } from "@/lib/utils";
import { useToast } from "@/lib/toast";
import { EmptyState, ErrorState, PageHeader } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageSkeleton } from "@/components/ui/skeleton";
import { SettingsNav } from "./SettingsConnect";

export default function UsersPage() {
  const me = useMe();
  const isAdmin = !!me.data?.isAdmin;
  const users = useUsers(isAdmin);
  const setDisabled = useSetUserDisabled();
  const { toast } = useToast();
  const [toDisable, setToDisable] = useState<AdminUser | null>(null);

  if (me.data && !isAdmin) {
    return (
      <div className="mx-auto max-w-3xl">
        <PageHeader title="Users" />
        <SettingsNav />
        <p className="text-sm text-muted-foreground">Only admins can manage users.</p>
      </div>
    );
  }

  const change = (u: AdminUser, disabled: boolean, done?: () => void) =>
    setDisabled.mutate(
      { id: u.id, disabled },
      {
        onSuccess: () => done?.(),
        onError: (err) => toast({ title: disabled ? "Could not disable" : "Could not enable", description: errorMessage(err), tone: "error" }),
      },
    );

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Users" description="Everyone with an account on this server." />
      <SettingsNav />

      {users.isLoading && <PageSkeleton />}
      {users.isError && <ErrorState error={users.error} retry={() => users.refetch()} />}
      {users.data && users.data.length === 0 && <EmptyState icon={UserRound} title="No users" />}
      {users.data && users.data.length > 0 && (
        <ul className="divide-y rounded-xl border bg-card">
          {users.data.map((u) => (
            <li key={u.id} className="flex items-center gap-3 px-4 py-3">
              <UserRound className="size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{u.displayName || u.username}</span>
                  <span className="text-xs text-muted-foreground">@{u.username}</span>
                  {u.isAdmin && <Badge variant="info">admin</Badge>}
                  {u.disabledAt && <Badge variant="destructive">disabled</Badge>}
                  {u.id === me.data?.id && <Badge variant="outline">you</Badge>}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  via {u.authProvider} · joined {timeAgo(u.createdAt)}
                  {u.disabledAt ? ` · disabled ${timeAgo(u.disabledAt)}` : ""}
                </div>
              </div>
              {u.id !== me.data?.id &&
                (u.disabledAt ? (
                  <Button variant="outline" size="sm" disabled={setDisabled.isPending} onClick={() => change(u, false)}>
                    Enable
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => setToDisable(u)}>
                    Disable
                  </Button>
                ))}
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!toDisable} onOpenChange={(o) => !o && setToDisable(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable {toDisable?.displayName || toDisable?.username}?</DialogTitle>
            <DialogDescription>
              They are signed out and cannot sign in again. All their API tokens and OAuth grants (connected MCP clients) are revoked; re-enabling the account does not restore them. Their notes stay.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToDisable(null)}>
              Cancel
            </Button>
            <Button variant="destructive" disabled={setDisabled.isPending} onClick={() => toDisable && change(toDisable, true, () => setToDisable(null))}>
              {setDisabled.isPending ? "Disabling…" : "Disable"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
